/** @file Resolves the mesh a tool works on in the viewport context: target, compiled ids, world points. */
import type { ToolContext } from '../../panels/viewport/viewportTypes';
import { selectionStore } from '../../stores/selectionStore';
import { compiledTarget, type CompiledTarget } from './compiledTarget';
import { meshTarget, type MeshTarget } from './meshTarget';
import { meshTargetStore } from './meshTargetStore';
import { meshWorldPoints } from './meshWorldPoints';
import { poseShownAttachment } from './poseShownAttachment';

/** A resolved mesh in the posed scene; `points` are world positions as drawn (2 per vertex). */
export interface MeshScene {
  ctx: ToolContext;
  target: MeshTarget;
  at: CompiledTarget;
  points: Float32Array;
}

/** Publishes the target to meshTargetStore only when it changes (idle runs every dirty frame). */
function publish(t: MeshTarget | null): void {
  const cur = meshTargetStore.getState().target;
  if (cur?.slot === t?.slot && cur?.attachment === t?.attachment) return;
  meshTargetStore.setState({ target: t === null ? null : { slot: t.slot, attachment: t.attachment } });
}

/** The mesh the selection names in the posed scene (selectionStore read fresh), or null. */
export function meshScene(ctx: ToolContext): MeshScene | null {
  const target = meshTarget(ctx.doc, selectionStore.getState(), poseShownAttachment(ctx.doc, ctx.data, ctx.pose));
  const at = target === null ? null : compiledTarget(ctx.doc, ctx.data, target);
  publish(at === null ? null : target);
  return target === null || at === null ? null : { ctx, target, at, points: meshWorldPoints(ctx.data, ctx.pose, at) };
}
